import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { Box, Text, Divider, Flex, Grid, GridItem, Link } from '@chakra-ui/react';
import { FS_SILENT_SUBSTITUTION, paymentOptions } from '@whitbread-eos/api';
import { formatCurrency, formatPrice, getCurrentReservationStorageData, useSemanticTypography, useFeatureSwitch } from '@whitbread-eos/utils';
import { useState, useEffect } from 'react';
import RoomCard from './RoomCard.component';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
function renderPaymentConfirmationText(paymentOption) {
  switch (paymentOption) {
    case paymentOptions.PAY_NOW:
      return 'booking.confirmation.paymentTaken';
    case paymentOptions.PAY_ON_ARRIVAL:
      return 'booking.confirmation.reservedWithCard';
    case paymentOptions.RESERVE_WITHOUT_CARD:
      return 'booking.confirmation.reservedWithoutCard';
    default:
      return paymentOption;
  }
}
export default function RoomDetails(_ref) {
  var data = _ref.data,
    basketReference = _ref.basketReference,
    currentLang = _ref.currentLang,
    t = _ref.t;
  var roomDetails = data.roomDetails,
    _data$donations = data.donations,
    donations = _data$donations === void 0 ? null : _data$donations,
    bookingTotalCost = data.bookingTotalCost,
    currency = data.currency,
    selectedPaymentOption = data.selectedPaymentOption,
    taxesMessage = data.taxesMessage,
    brand = data.brand;
  var baseDataTestId = 'RoomDetailsSection';
  var _useState = useState([]),
    _useState2 = _slicedToArray(_useState, 2),
    cardsExpanded = _useState2[0],
    setCardsExpanded = _useState2[1];
  var _useState3 = useState(true),
    _useState4 = _slicedToArray(_useState3, 2),
    showExpanded = _useState4[0],
    setShowExpanded = _useState4[1];
  var getTypographyProps = useSemanticTypography();
  var showExpandCollapseButton = roomDetails.length !== 1;
  var isCardsExpandedIndividually = cardsExpanded.every(element => !element.expanded);
  var isCardsCollapsedIndividually = cardsExpanded.every(element => element.expanded);
  var isSilentFeatureFlagEnabled = useFeatureSwitch({
    featureSwitchKey: FS_SILENT_SUBSTITUTION
  });
  var currentReservationRoomData = isSilentFeatureFlagEnabled ? getCurrentReservationStorageData(basketReference) : {};
  useEffect(() => {
    if (isCardsExpandedIndividually) {
      setShowExpanded(true);
    }
    if (isCardsCollapsedIndividually) {
      setShowExpanded(false);
    }
  }, [isCardsExpandedIndividually, isCardsCollapsedIndividually]);
  if (!roomDetails.length) {
    return null;
  }
  var calcRoomsTotal = () => {
    var roomsTotal = 0;
    roomDetails.forEach(room => roomsTotal += room.roomPrice);
    return roomsTotal;
  };
  var calcExtrasTotal = () => {
    var extrasTotal = 0;
    roomDetails.forEach(room => {
      var _room$packages;
      room === null || room === void 0 || (_room$packages = room.packages) === null || _room$packages === void 0 || _room$packages.forEach(pkg => {
        var _room$ratesPerNight;
        return extrasTotal += !pkg.description.includes('Charity') ? pkg.computedPrice * (room === null || room === void 0 || (_room$ratesPerNight = room.ratesPerNight) === null || _room$ratesPerNight === void 0 ? void 0 : _room$ratesPerNight.length) : 0;
      });
    });
    return extrasTotal;
  };
  return _jsxs(Box, _objectSpread(_objectSpread({}, contentWrapperStyle), {}, {
    "data-testid": baseDataTestId,
    children: [_jsxs(Box, _objectSpread(_objectSpread({}, roomDetailsTitleContainer), {}, {
      children: [_jsx(Text, _objectSpread(_objectSpread(_objectSpread({}, roomDetailsTitleLayoutStyles), getTypographyProps(roomDetailsTitleLegacyTypography, roomDetailsTitleSemanticTypography)), {}, {
        sx: {
          '@media print': {
            display: 'none'
          }
        },
        children: t('booking.roomdetails.title')
      })), showExpandCollapseButton && _jsx(Link, _objectSpread(_objectSpread({
        onClick: () => handleExpandCollapse()
      }, expandCollapseButtonStyle), {}, {
        children: showExpanded ? t('booking.roomdetails.expandAll') : t('booking.roomdetails.collapseAll')
      }))]
    })), renderRoomCards(), _jsxs(Box, {
      display: "none",
      sx: {
        '@media print': {
          display: 'block'
        }
      },
      children: [_jsx(Divider, {
        my: "lg",
        borderBottomWidth: "2px"
      }), _jsxs(Flex, {
        justify: "space-between",
        mb: "md",
        children: [_jsx(Text, {
          children: t('account.dashboard.roomsTotal')
        }), _jsx(Text, {
          fontWeight: "bold",
          children: formatPrice(formatCurrency(currency), calcRoomsTotal().toFixed(2), currentLang)
        })]
      }), _jsxs(Flex, {
        justify: "space-between",
        mb: "md",
        children: [_jsx(Text, {
          children: t('account.dashboard.extrasTotal')
        }), _jsx(Text, {
          fontWeight: "bold",
          children: formatPrice(formatCurrency(currency), calcExtrasTotal().toFixed(2), currentLang)
        })]
      }), (donations === null || donations === void 0 ? void 0 : donations.amount) && _jsxs(Flex, {
        justify: "space-between",
        mb: "md",
        children: [_jsx(Text, {
          children: t('booking.confirmation.donationMessage')
        }), _jsx(Text, {
          fontWeight: "bold",
          children: formatPrice(formatCurrency(currency), donations === null || donations === void 0 ? void 0 : donations.amount, currentLang)
        })]
      }), _jsxs(Grid, {
        templateColumns: "repeat(2, 1fr)",
        mb: "md",
        children: [_jsxs(GridItem, {
          children: [_jsxs(Text, {
            fontWeight: "bold",
            mb: "md",
            children: [t('booking.confirmation.totalCost'), ":"]
          }), _jsx(Text, {
            children: t(renderPaymentConfirmationText(selectedPaymentOption))
          })]
        }), _jsxs(GridItem, {
          textAlign: "right",
          children: [_jsx(Text, {
            fontWeight: "bold",
            fontSize: "4xl",
            children: formatPrice(formatCurrency(currency), bookingTotalCost.toFixed(2), currentLang)
          }), !!(taxesMessage !== null && taxesMessage !== void 0 && taxesMessage.length) && _jsx(Text, {
            children: taxesMessage
          })]
        })]
      }), _jsx(Divider, {
        my: "lg",
        borderBottomWidth: "2px"
      })]
    })]
  }));
  function handleExpandCollapse(roomNumber) {
    setCardsExpanded(prevState => prevState.map(cardItem => {
      var expanded = !cardItem.expanded;
      if (roomNumber && cardItem.roomNumber !== roomNumber) {
        expanded = cardItem.expanded;
      }
      return _objectSpread(_objectSpread({}, cardItem), {}, {
        expanded
      });
    }));
  }
  function renderRoomCards() {
    return roomDetails.map((room, index) => {
      var _currentReservationRo, _currentReservationRo2, _currentReservationRo3, _cardsExpanded$, _cardsExpanded$index;
      var roomData = isSilentFeatureFlagEnabled && (currentReservationRoomData === null || currentReservationRoomData === void 0 || (_currentReservationRo = currentReservationRoomData.value[index]) === null || _currentReservationRo === void 0 ? void 0 : _currentReservationRo.silentSubstitution) && _objectSpread({}, _objectSpread(_objectSpread({}, room), {}, {
        roomType: (_currentReservationRo2 = currentReservationRoomData === null || currentReservationRoomData === void 0 || (_currentReservationRo3 = currentReservationRoomData.value[index]) === null || _currentReservationRo3 === void 0 ? void 0 : _currentReservationRo3.roomLabelCode) !== null && _currentReservationRo2 !== void 0 ? _currentReservationRo2 : '',
        roomTypeDescription: null
      })) || room;
      return _jsx(RoomCard, {
        t: t,
        room: roomData,
        currency: currency,
        roomNumber: index + 1,
        currentLang: currentLang,
        taxesMessage: taxesMessage,
        brand: brand,
        isCardExpanded: roomDetails.length === 1 ? !((_cardsExpanded$ = cardsExpanded[0]) !== null && _cardsExpanded$ !== void 0 && _cardsExpanded$.expanded) : (_cardsExpanded$index = cardsExpanded[index]) === null || _cardsExpanded$index === void 0 ? void 0 : _cardsExpanded$index.expanded,
        cardsExpanded: cardsExpanded,
        setCardsExpanded: setCardsExpanded,
        handleExpandCollapse: handleExpandCollapse
      }, "room-".concat(index + 1, "-key"));
    });
  }
}
var contentWrapperStyle = {
  w: 'full',
  mb: '2xl'
};
var roomDetailsTitleLayoutStyles = {
  mb: 'md'
};
var roomDetailsTitleLegacyTypography = {
  fontSize: '2xl',
  fontWeight: 'semibold'
};
var roomDetailsTitleSemanticTypography = {
  textStyle: 'heading-m'
};
var expandCollapseButtonStyle = {
  fontSize: '0.875rem',
  fontWeight: 'semibold',
  textDecoration: 'underline',
  lineHeight: '1.25rem',
  color: '#511E62'
};
var roomDetailsTitleContainer = {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between'
};